import React, { useState, useEffect } from 'react';
import PasswordInput from '../components/PasswordInput';
import { CheckCircle2, Clock, MapPin, Calendar, User, LogOut, Star } from 'lucide-react';
import { supabase, setSession } from '../services/supabaseClient';
import PayrollDashboard from '../components/PayrollDashboard';
import BookingDetails from '../components/BookingDetails';

const EmployeePage = ({ employee: employeeProp, onLogout }) => {
const [employee, setEmployee] = useState(employeeProp || null);
const [loginData, setLoginData] = useState({ email: '', password: '' });
const [loginError, setLoginError] = useState('');
const [bookings, setBookings] = useState([]);
const [acceptances, setAcceptances] = useState([]);
const [loading, setLoading] = useState(false);
const [accepting, setAccepting] = useState(null);
const [message, setMessage] = useState('');

useEffect(() => {
const saved = localStorage.getItem('galax_employee');
if (saved && localStorage.getItem('galax_token')) setEmployee(JSON.parse(saved));
}, []);

useEffect(() => {
if (employee) {
loadBookings();
loadMyAcceptances();
}
}, [employee]); // eslint-disable-line react-hooks/exhaustive-deps

const loadBookings = async () => {
setLoading(true);
try {
const today = new Date().toISOString().split('T')[0];
const { data } = await supabase
.from('bookings')
.select('*')
.eq('status', 'upcoming')
.gte('date', today)
.order('date', { ascending: true });
setBookings(data || []);
} catch (error) {
console.error('Error loading bookings:', error);
}
setLoading(false);
};

const loadMyAcceptances = async () => {
try {
const { data } = await supabase
.from('shift_acceptances')
.select('*')
.eq('employee_id', employee.id);
setAcceptances(data || []);
} catch (error) {
console.error('Error loading acceptances:', error);
}
};

const loadAcceptancesForBooking = async (bookingId) => {
try {
const { data } = await supabase
.from('shift_acceptances')
.select('*')
.eq('booking_id', bookingId);
return data || [];
} catch {
return [];
}
};

const handleLogin = async () => {
  setLoginError('');
  try {
    const res = await fetch('/.netlify/functions/auth-login', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: loginData.email, password: loginData.password })
    });
    const out = await res.json().catch(() => ({}));
    if (!res.ok || !out.user || out.user.role !== 'employee') { setLoginError('Fel email eller lösenord'); return; }
    setSession(out.token);
    localStorage.setItem('galax_employee', JSON.stringify(out.user));
    setEmployee(out.user);
  } catch (error) {
    setLoginError('Något gick fel, försök igen');
  }
};

const handleLogout = () => {
localStorage.removeItem('galax_employee');
localStorage.removeItem('galax_token');
setEmployee(null);
if (onLogout) onLogout();
};

const handleAcceptShift = async (booking) => {
setAccepting(booking.id);
try {
const response = await fetch('/.netlify/functions/accept-shift', {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({
bookingId: booking.id,
employeeId: employee.id,
employeeName: employee.name,
employeeEmail: employee.email
})
});
const result = await response.json();
if (response.ok) {
setMessage('✅ Du har accepterat passet! Kolla din email för detaljer.');
await loadBookings();
await loadMyAcceptances();
} else {
setMessage(`❌ ${result.error}`);
await loadBookings();
await loadMyAcceptances();
}
} catch (error) {
setMessage('❌ Något gick fel, försök igen');
}
setAccepting(null);
setTimeout(() => setMessage(''), 5000);
};

const serviceNames = {
standard: 'Standardstädning', deep: 'Djupstädning',
moving: 'Flyttstädning', office: 'Kontorsstädning', help: 'Flytthjälp'
};

const inputStyle = {
width: '100%', padding: '12px 16px', borderRadius: '12px',
border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4',
color: '#1a1a2e', outline: 'none', fontSize: '16px'
};

// LOGIN SCREEN
if (!employee) {
return (
<div className="min-h-screen flex items-center justify-center px-4"
style={{ backgroundColor: '#f8f7f4' }}>
<div className="w-full max-w-md">
<div className="text-center mb-8">
<div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
style={{ backgroundColor: '#1a1a2e' }}>
<Star className="w-10 h-10" style={{ color: '#C9A84C' }} fill="#C9A84C"/>
</div>
<h1 className="text-3xl font-bold mb-1" style={{ color: '#1a1a2e' }}>GALAX</h1>
<p className="text-sm tracking-widest font-semibold" style={{ color: '#C9A84C' }}>PERSONAL PORTAL</p>
</div>
<div className="p-8 rounded-2xl" style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9', boxShadow: '0 4px 30px rgba(0,0,0,0.08)' }}>
<h2 className="text-xl font-bold mb-6" style={{ color: '#1a1a2e' }}>Logga in</h2>
<div className="space-y-4">
<div>
<label className="block text-sm font-bold mb-2" style={{ color: '#1a1a2e' }}>Email</label>
<input type="email" value={loginData.email}
onChange={(e) => setLoginData(p => ({ ...p, email: e.target.value }))}
placeholder="din@email.com" style={inputStyle}
onKeyPress={(e) => e.key === 'Enter' && handleLogin()}/>
</div>
<div>
<label className="block text-sm font-bold mb-2" style={{ color: '#1a1a2e' }}>Lösenord</label>
<PasswordInput value={loginData.password}
onChange={(e) => setLoginData(p => ({ ...p, password: e.target.value }))}
placeholder="••••••••" style={inputStyle}
onKeyPress={(e) => e.key === 'Enter' && handleLogin()}/>
</div>
{loginError && <p className="text-sm" style={{ color: '#dc2626' }}>{loginError}</p>}
<button onClick={handleLogin}
className="w-full py-3 rounded-full font-bold transition-all hover:scale-105"
style={{ backgroundColor: '#1a1a2e', color: '#C9A84C', border: '2px solid #C9A84C' }}>
Logga In
</button>
</div>
</div>
</div>
</div>
);
}

// EMPLOYEE DASHBOARD
return (
<div className="min-h-screen pt-24 pb-20 px-4" style={{ backgroundColor: '#f8f7f4' }}>
<div className="max-w-4xl mx-auto">

{/* Header */}
<div className="flex justify-between items-center mb-8">
<div>
<h1 className="text-3xl font-bold" style={{ color: '#1a1a2e' }}>
Hej, {employee.name}! 👋
</h1>
<p className="text-sm" style={{ color: '#888' }}>Personal Portal — GALAX Elite Städ</p>
</div>
<button onClick={handleLogout}
className="px-4 py-2 rounded-full font-semibold flex items-center gap-2 text-sm"
style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>
<LogOut className="w-4 h-4"/><span>Logga Ut</span>
</button>
</div>

{/* Message */}
{message && (
<div className="p-4 rounded-xl mb-6 text-center font-semibold"
style={{ backgroundColor: message.startsWith('✅') ? '#dcfce7' : '#fee2e2', color: message.startsWith('✅') ? '#16a34a' : '#dc2626' }}>
{message}
</div>
)}

{/* My accepted shifts */}
{acceptances.length > 0 && (
<div className="mb-8">
<h2 className="text-xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Mina Accepterade Pass</h2>
<div className="space-y-3">
{bookings.filter(b => acceptances.some(a => a.booking_id === b.id)).map(booking => (
<div key={booking.id} className="p-4 rounded-xl flex items-center justify-between"
style={{ backgroundColor: '#dcfce7', border: '2px solid #86efac' }}>
<div>
<p className="font-bold" style={{ color: '#16a34a' }}>✅ {serviceNames[booking.service]}</p>
<p className="text-sm" style={{ color: '#166534' }}>{booking.date} kl {booking.time} — {booking.address}, {booking.city}</p>
<div className="mt-3"><BookingDetails details={booking.details} /></div>
</div>
<CheckCircle2 className="w-6 h-6" style={{ color: '#16a34a' }}/>
</div>
))}
</div>
</div>
)}

{/* Available shifts */}
<h2 className="text-xl font-bold mb-4" style={{ color: '#1a1a2e' }}>
Tillgängliga Pass ({bookings.filter(b => !acceptances.some(a => a.booking_id === b.id)).length})
</h2>

{/* Lön */}
<div className="mt-12">
<h2 className="text-xl font-bold mb-6" style={{ color: '#1a1a2e' }}>💰 Min Lön</h2>
<PayrollDashboard currentUser={employee} />
</div>

{loading ? (
<div className="text-center py-12" style={{ color: '#888' }}>Laddar pass...</div>
) : bookings.length === 0 ? (
<div className="text-center py-12 rounded-2xl" style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9' }}>
<p className="text-lg font-semibold mb-2" style={{ color: '#1a1a2e' }}>Inga tillgängliga pass just nu</p>
<p style={{ color: '#888' }}>Kom tillbaka senare — vi meddelar när nya pass finns!</p>
</div>
) : (
<div className="space-y-4">
{bookings
.filter(b => !acceptances.some(a => a.booking_id === b.id))
.map((booking) => {
const alreadyAccepted = acceptances.some(a => a.booking_id === booking.id);
const isFull = (booking.accepted_count || 0) >= (booking.max_workers || 2);

return (
<ShiftCard
key={booking.id}
booking={booking}
serviceNames={serviceNames}
alreadyAccepted={alreadyAccepted}
isFull={isFull}
accepting={accepting}
onAccept={handleAcceptShift}
loadAcceptancesForBooking={loadAcceptancesForBooking}
/>
);
})}
</div>
)}
</div>
</div>
);
};

const ShiftCard = ({ booking, serviceNames, alreadyAccepted, isFull, accepting, onAccept, loadAcceptancesForBooking }) => {
const [shiftAcceptances, setShiftAcceptances] = useState([]);

useEffect(() => {
loadAcceptancesForBooking(booking.id).then(setShiftAcceptances);
}, [booking.id, booking.accepted_count]); // eslint-disable-line react-hooks/exhaustive-deps

return (
<div className="p-6 rounded-2xl transition-all"
style={{ backgroundColor: '#fff', border: `2px solid ${isFull ? '#e8e4d9' : '#C9A84C'}`, opacity: isFull ? 0.7 : 1 }}>
<div className="flex flex-wrap justify-between items-start gap-4 mb-4">
<div>
<div className="flex items-center gap-2 mb-1">
<h3 className="text-xl font-bold" style={{ color: '#1a1a2e' }}>
{serviceNames[booking.service]}
</h3>
{isFull && (
<span className="text-xs font-bold px-2 py-1 rounded-full"
style={{ backgroundColor: '#fee2e2', color: '#dc2626' }}>FULLT</span>
)}
</div>
</div>
<div className="text-right">
<p className="text-sm font-bold" style={{ color: '#888' }}>
{shiftAcceptances.length} / {booking.max_workers || 2} accepterade
</p>
</div>
</div>

<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 text-sm">
<div className="flex items-center gap-2" style={{ color: '#555' }}>
<Calendar className="w-4 h-4" style={{ color: '#C9A84C' }}/>
<span>{booking.date} kl {booking.time}</span>
</div>
<div className="flex items-center gap-2" style={{ color: '#555' }}>
<MapPin className="w-4 h-4" style={{ color: '#C9A84C' }}/>
<span>{booking.address ? `${booking.address}, ${booking.city}` : booking.city}</span>
</div>
<div className="flex items-center gap-2" style={{ color: '#555' }}>
<Clock className="w-4 h-4" style={{ color: '#C9A84C' }}/>
<span>{booking.sqm} kvm</span>
</div>
{shiftAcceptances.length > 0 && (
<div className="flex items-center gap-2" style={{ color: '#555' }}>
<User className="w-4 h-4" style={{ color: '#C9A84C' }}/>
<span>{shiftAcceptances.map(a => a.employee_name).join(', ')}</span>
</div>
)}
</div>

{booking.notes && (
<div className="p-3 rounded-xl mb-4 text-sm" style={{ backgroundColor: '#fef9ec', border: '1px solid #fde68a' }}>
<span style={{ color: '#92400e' }}>💬 "{booking.notes}"</span>
</div>
)}

{!isFull && !alreadyAccepted && (
<button
onClick={() => onAccept(booking)}
disabled={accepting === booking.id}
className="w-full py-3 rounded-full font-bold transition-all hover:scale-105"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
{accepting === booking.id ? 'Accepterar...' : '✅ Ta detta pass'}
</button>
)}

{alreadyAccepted && (
<div className="w-full py-3 rounded-full font-bold text-center"
style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>
✅ Du har accepterat detta pass
</div>
)}

{isFull && !alreadyAccepted && (
<div className="w-full py-3 rounded-full font-bold text-center"
style={{ backgroundColor: '#f3f4f6', color: '#9ca3af' }}>
Detta pass är fullt
</div>
)}
</div>
);
};

export default EmployeePage;